// Card 1218200003007957: the web public profile (#/u/:handle) renders REAL
// per-user data instead of floor-local scraps. renderPublicProfile() reads
// four things off USERS[handle]/LISTINGS that mapSellerToUser
// (floorTransport.ts) has never had a source for or only ever gets a
// FRACTION of:
//
//   1. u.followers / u.following — mapSellerToUser hardcodes both to 0.
//   2. u.bio — mapSellerToUser hardcodes "".
//   3. Active Items tile (theirs.length) and the "Live Listings" grid below
//      it — both `LISTINGS.filter((l) => l.seller === handle)`, and LISTINGS
//      only ever held the boot-time platform-wide 100-row snapshot
//      (GET /api/listings?limit=100), so a seller with more than a
//      fractional share of that snapshot showed an incomplete storefront.
//      Since 2026-09-27 the floor loads every active listing
//      (fetchAllListingRows, floorTransport.ts), so the merge below fills
//      only what the floor did not load: a listing created after the page
//      loaded, one on a later page that failed, or one past the page bound.
//
// Same SHAPE of fix as ratingFixups.ts (a MutationObserver-driven, one-shot-
// per-handle patch of the CURRENT profile view, after app.js has already
// painted its own honest defaults) and the same reason it is a separate
// module rather than a third branch bolted onto that one: ratingFixups is
// merged/reviewed and the task brief marks it off-limits, and — for the
// listings half below — this is a different SHAPE of problem again: it does
// not just write text into an existing node, it has to feed newly-learned
// rows through the shared __gbzMergeListing bridge and then get Blake's own
// renderer to run again, which ratingFixups never needs to do.
//
// ENDPOINTS WIRED:
//   GET /api/users/:id           — live-recomputed followerCount/
//                                  followingCount + bio (api-server/src/
//                                  routes/users.ts:706). applyProfilePrivacy
//                                  never strips these three fields (only
//                                  location/tradeCount/points/online-status
//                                  are privacy-gated), so they are always
//                                  present when the request succeeds.
//   GET /api/users/:id/listings  — newest-first, active-only, optionalAuth
//                                  (routes/users.ts:728). Returns a bare
//                                  array of toPublicListing() rows — the
//                                  SAME raw `listings` table columns
//                                  GET /api/listings selects into its own
//                                  `listing` sub-object, just not wrapped in
//                                  a `{listing, seller}` envelope and with no
//                                  seller info at all — so each row is
//                                  structurally an ApiListingRow["listing"],
//                                  reused as-is below.
//
// STRETCH (collections/stats + wants) is explicitly out of tonight's scope —
// see the task brief's "drop the stretch rather than risk the core."
//
// STRUCTURAL MATCHING, same convention as ratingFixups.ts's own header: the
// profile root is found via `[data-follow]` (renderPublicProfile's own
// marker; renderProfile — the signed-in visitor's OWN page — never emits
// it), and the guard attribute below is set on THAT element rather than on
// any of the things this module patches, because one of those things (the
// Followers/Following stat cells) gets REPLACED wholesale by
// demoTextFixups.ts's neutralizePublicFollowerTriggers — see this file's
// own "🔴 MANDATORY COMPANION" note below for why that replacement exists,
// and why `[data-follow]` is the one node in this view neither fixup ever
// touches.
//
// 🔴 MANDATORY COMPANION, SHIPPED IN THE SAME PR (demoTextFixups.ts, not
// here): app.js's Followers/Following stat tiles double as a
// `[data-follist]` button that opens a modal whose roster
// (renderFolList, app.js:3523) is `Object.values(USERS).filter(...)` — every
// OTHER real seller on the floor, presented as this person's followers.
// demoTextFixups.ts's fixFollowerRoster already suppresses that roster, but
// ONLY while the count it sits under reads 0, and its own comment says so:
// a non-zero count over a fabricated roster is a state it cannot honestly
// repair, so it warns and leaves the roster standing. The moment THIS
// module starts painting real, non-zero counts, that warned-about day
// arrives — so demoTextFixups.ts's neutralizePublicFollowerTriggers
// converts the two stat-link buttons into plain, inert `.stat` cells on the
// public profile, closing the only path to that modal. See its own header
// for the full reasoning; see profileFixups.test.ts for the test proving
// the two modules' combined effect (real counts render, and the roster is
// unreachable).
//
// A SELLER WITH NO RESOLVABLE ID (not in the `userIds` map installFloorListings
// returns) is a SILENT no-op, not a warning — unlike ratingFixups.ts, which
// warns loudly on the same miss. Deliberate: the task brief scopes that gap
// ("a fourth USERS producer, or a handle whose two producers have drifted")
// to a future server card, not tonight's fix, and every handle this module
// will ever see in practice already came through the same two producers
// ratingFixups.ts's own comment names.
//
// ============================================================================
// ROUND 2 (card 1218200003007957's promoted stretch + card 1218154840765281's
// storefront-badge item): two more fabrications on the SAME view, wired the
// same way.
//
//   THE TIER CHIP (Nick's Q1 ruling, card 1218128111112081, third strike on
//   ROOKIE): app.js:1478's `<span class="level-chip">${u.level.toUpperCase()}</span>`
//   prints mapSellerToUser's `traderLevel ?? ""` VERBATIM — the raw
//   rookie/verified/hall_of_fame enum, one of the two retired vocabularies
//   (the other, Merchant/Magnate/Mogul, never reached this chip but is
//   retired all the same). GET /api/users/:id — already fetched by PART (a)
//   above for followers/following/bio — now also returns `gemTier` (the
//   six-gem NAME string, server-computed via getGemTierFromPoints — a copy
//   of data.js's TIERS, pinned to it by gemTiers.test.ts) and `level` (1-60,
//   getLevelFromPoints(totalAccumulatedPoints), the SAME helper GET
//   /me/wallet uses).
//
//   🔴 F1 (adversarial review): `gemTier` and `level` are gated DIFFERENTLY
//   server-side. `level` is stripped by applyProfilePrivacy's showPoints
//   branch (routes/users.ts) — same as `points`/`totalAccumulatedPoints`.
//   `gemTier` is NOT — the badge is the public storefront's point (landing
//   copy: "your tier shows on your profile"), so it rides the response
//   ungated. patchLevelChip below reads `summary.gemTier` FIRST; the local
//   getGemTier(level) mapping is only the FALLBACK, for an older API
//   response that has `level` but not yet `gemTier`. When both are absent
//   (fetch in flight, fetch failed, or a privacy-hidden `level` on a
//   pre-gemTier response) the chip is left exactly as Blake's server-
//   rendered markup painted it: never blanked. Riding the existing fetch
//   means the badge needs no fetch, cache, or guard attribute of its own —
//   same idempotent-write and cache-hit-repatch discipline the
//   followers/following writes already have.
//
//   COLLECTIONS (the dropped stretch): the Collection Value tile
//   (`.stats-row.stats-7`'s 4th cell) and the Collection section's own count
//   chip + value line (`.exch-banner-toprow`'s `.count-chip`/`.sec-count`)
//   all read `collectionValueOf(handle)`/`collectionFor(handle).length` —
//   floor-local, LISTINGS-derived numbers with the same fractional-snapshot
//   problem PART (b) above already fixed for Active Items/Live Listings.
//   GET /api/collections/stats/:userId (public, no auth) now returns
//   `{ collectionValue, itemCount, ... }` — itemCount added this round.
//   Both fields are filtered by the SAME hidden-statuses-only rule
//   (PUBLICLY_HIDDEN_LISTING_STATUSES — removed/rejected; traded and sold
//   stay counted, matching mobile) so the two numbers shown side by side
//   never disagree about what they're counting. Same fetch-once/cache/
//   idempotent-write/never-paint-on-failure shape as PART (a)'s stats fetch,
//   just a second cache keyed the same way.
//
//   THE GRID ITSELF (F3, adversarial review — no longer residual): PART (d)
//   below fetches GET /collections/user/:userId (the same public,
//   unauthenticated route the numbers above are computed from) and merges
//   each row into PUBLIC_EXTRAS[handle] via the bridge's own
//   __gbzMergeCollectionExtra, so collectionFor(handle) — and with it the
//   grid, the Sold/Traded/Pending tabs, and their category chips — render
//   the same true data the count/value numbers already did. PUBLIC_EXTRAS,
//   not LISTINGS, is the merge target because collectionFor's LISTINGS-
//   derived `live` branch can only ever label a row ACTIVE or IN
//   COLLECTION — see mapPublicCollectionRow and __gbzMergeCollectionExtra's
//   own headers (floorTransport.ts) for the full reasoning. DISCLOSED GAP,
//   SMALLER THAN BEFORE: a row whose listing is paused/under_review/
//   unlisted has no bucket in app.js's six-tab UI and is silently skipped,
//   same posture this module already takes for an unrenderable category or
//   an unresolvable seller id.
// ============================================================================
// ROUND 3 (card 1218203357125435, "no literals" — Tyler's brief, card
// 1218202996944129): three more items off that census, all on this same view.
//
//   THE LEVEL-CHIP REGRESSION. patchLevelChip used to overwrite the WHOLE
//   chip on every view. Correct on the public chip (bare gem word) — wrong on
//   the SELF view's chip (app.js renderProfile: "<TIER> · <N PTS>"), where an
//   unconditional overwrite silently dropped real, already-rendered points.
//   Fixed by threading `view` through and, on 'self' only, replacing just the
//   tier token before the first " · " — see patchLevelChip's own header.
//
//   TRADES, the seventh consumer of the four honest-unknown fields
//   (floorTransport.ts: mapSellerToUser/mapDealCounterparts/mapCommentAuthors
//   now emit `trades: null` rather than 0 for the two non-seller producers).
//   GET /api/users/:id's `tradeCount` (optional — privacy can delete the key,
//   see PublicUserProfile's own comment) patches the Trades tile the moment
//   either profile is fetched directly, the same "even a counterpart/
//   commenter-only USERS entry gets a real number once you visit THEIR page"
//   upgrade path followers/following/bio already had.
//
//   THE PUBLIC "IS LOOKING FOR" SECTION — PART (e) below. USER_LOOKING is
//   cleared to `{}` at boot (floorTransport.ts) and nothing had repopulated
//   it since: real sellers got silent absence where Blake's six-handle
//   fixture used to fabricate. GET /api/wants/:userId is real and public
//   (routes/wants.ts), so PART (e) wires it per profile view, through the
//   bridge's new __gbzSetUserLooking — see that function's own header
//   (floorTransport.ts's bridgeScriptSource) for the shorter, deduped label
//   shape and why the fixture can never resurface through this path.
// ============================================================================

import { mapListingRow, mapPublicCollectionRow, RENDERABLE_CATEGORY_SLUGS, type ApiListingRow, type ApiWant, type DemoListing, type DemoUser, type PublicCollectionRow, type PublicCollectionExtraItem } from "./floorTransport";
import { getGemTier } from "../lib/gemTiers";

type AuthFetch = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

/** GET /api/users/:id's shape, trimmed to the six fields this module reads.
 *  `bio` mirrors the `users.bio` column (lib/db/src/schema/users.ts),
 *  nullable for a seller who never set one — Blake's own template already
 *  renders that honestly (`esc(u.bio || "")`), so this module matches it
 *  rather than inventing placeholder copy. `level` and `gemTier` (ROUND 2)
 *  are null/absent when not known yet (see patchLevelChip below) — never a
 *  fabricated value. `gemTier` is F1's addition (adversarial review): unlike
 *  `level`, it is never privacy-gated server-side — see patchLevelChip's own
 *  comment for why the chip prefers it over deriving the gem from `level`.
 *
 *  `tradeCount` (ROUND 3, card 1218203357125435) is OPTIONAL, and for a
 *  different reason than `level`/`gemTier`'s "not computed yet": serializers.ts's
 *  applyProfilePrivacy DELETES the key outright when showTradeCount is false
 *  (see users.ts's own route comment), so an absent field here can mean
 *  either "the viewer isn't allowed to see it" or "this server predates the
 *  field" — both cases patchProfileStats must leave the Trades tile exactly
 *  as mapSellerToUser/mapDealCounterparts/mapCommentAuthors already painted
 *  it (a real number for a seller, "—" otherwise), never overwrite it with
 *  something invented. */
interface PublicUserProfile {
  followerCount: number;
  followingCount: number;
  bio: string | null;
  level?: number | null;
  gemTier?: string | null;
  tradeCount?: number;
}

/** Marks the profile as "counts/bio fetched (or in-flight) for this handle",
 *  set on `[data-follow]` for the same reason ratingFixups.ts's
 *  FIXED_FOR_ATTR lives on its OWN tile's container: navigating to a NEW
 *  handle replaces the whole subtree, wiping the attribute along with it, so
 *  a fresh handle always refetches. Per-handle rather than a boolean for the
 *  identical reason ratingFixups.ts gives.
 *
 *  🔴 F1 (adversarial review, card 1218200003007957): this DOM attribute
 *  guards the FETCH only — it says nothing about what to paint when a
 *  re-render wipes the node this attribute lives on. `profileStatsCache`
 *  (threaded alongside `listingsFetchedFor`, see applyProfileStatsFixup's
 *  own header) is what stops that re-render from ever showing Blake's
 *  hardcoded zeros again; this attribute still exists purely to prevent a
 *  second concurrent fetch while the first is in flight. */
const FIXED_FOR_ATTR = "data-gbz-profile-for";

/** Which of app.js’s TWO profile renderers painted the view we are on.
 *
 * 🔑 They are different DOMs and only one of them used to get fixed. The
 * signed-in owner’s own page (`renderProfile`) never emits `[data-follow]`,
 * so every fixup anchored on that selector — this module, ratingFixups, and
 * demoTextFixups’ follower-trigger neutralizer — silently bailed on it and
 * left Blake's hardcoded zeros and literals standing on the one profile a
 * user looks at most: their own. */
export type ProfileView = 'public' | 'self';

interface ProfileTile {
  /** The node the guard attribute lives on: the `[data-follow]` button on the
   *  public view, the `#edit-profile` button on the owner’s own. Both are
   *  stable across everything this module and
   *  demoTextFixups.ts’s neutralizePublicFollowerTriggers do to the view,
   *  which is why the guard lives here and not on a stat cell that can be
   *  replaced out from under it. */
  anchor: Element;
  view: ProfileView;
  wrap: Element;
  handle: string;
  /** Undefined means "no resolvable id" — see this file's header for why
   *  that is a silent no-op rather than a warning. */
  userId: string | undefined;
}

/** The owner’s handle, read off a stat tile the self view still carries.
 *
 * `renderProfile` writes `data-follist="${store.me}:followers"` on its
 * Followers/Following cells, so the prefix IS the signed-in handle. Read here
 * rather than from `store.me` directly because this module only ever touches
 * the DOM, and because it fails closed: no tile, no handle, no patch. */
function selfHandleFrom(wrap: Element): string | null {
  const el = wrap.querySelector("[data-follist]");
  const raw = el?.getAttribute("data-follist") ?? "";
  const handle = raw.split(":")[0]?.trim();
  return handle ? handle : null;
}

function findProfileTile(
  doc: Document,
  userIds: Record<string, string>,
  selfUserId: string | null,
): ProfileTile | null {
  // PUBLIC first: `[data-follow]` is emitted by renderPublicProfile alone.
  const followBtn = doc.querySelector(".profile.wrap [data-follow]");
  const followHandle = followBtn?.getAttribute("data-follow");
  if (followBtn && followHandle) {
    const wrap = followBtn.closest(".profile.wrap");
    if (!wrap) return null;
    return { anchor: followBtn, view: 'public', wrap, handle: followHandle, userId: userIds[followHandle] };
  }

  /* SELF. `#edit-profile` is emitted by renderProfile alone (verified by
     position against the bundle: renderProfile has it once, renderPublic
     Profile never). The id comes from the AUTH SESSION, not from `userIds` —
     that map is built from the boot-time floor snapshot, so it only contains
     the signed-in user if they happen to be a seller in it, and the fix would
     silently do nothing for anyone else. */
  const editBtn = doc.querySelector(".profile.wrap #edit-profile");
  if (!editBtn) return null; // on neither profile view this pass
  const wrap = editBtn.closest(".profile.wrap");
  if (!wrap || !selfUserId) return null;
  const handle = selfHandleFrom(wrap);
  if (!handle) return null;
  return { anchor: editBtn, view: 'self', wrap, handle, userId: selfUserId };
}

/** Matches the TRAILING "N followers" clause of app.js:1481's
 *  `<p class="sub">Member since ${u.memberSince} · ${fmtCount(u.trades)} trades · ${fmtCount(u.followers)} followers</p>`
 *  — anchored at the end rather than against the whole sentence so this
 *  keeps working whether or not demoTextFixups.ts's fixDanglingMemberSince
 *  has already trimmed a missing "Member since" label off the FRONT of the
 *  same string this pass. The two fixups edit opposite ends of one line and
 *  neither depends on the other having run.
 *
 *  🔑 MATCHES THE UNKNOWN STATE TOO, not just a digit. mapSellerToUser (and
 *  the two other DemoUser producers) now emit `followers: null` rather than
 *  0 — see floorTransport.ts — and app.js's own render site formats that as
 *  the em dash (UNKNOWN_METRIC, data.js), so the FIRST paint of this line can
 *  read "… · — followers" before this fixup's fetch ever resolves. A
 *  digit-only pattern would miss that shape entirely and warn on every
 *  perfectly normal boot, so the count half also accepts the same dash. */
const FOLLOWERS_LINE_RE = /(\d+|—)(\s*followers)\s*$/;

/** Finds a stat cell by its LABEL, not by `data-follist` or by tag — the same
 *  reason ratingFixups.ts's findRatingsTile matches on label text rather than
 *  position: after demoTextFixups.ts's neutralizePublicFollowerTriggers has
 *  run, the Followers/Following cell is a plain `<div class="stat">`, not the
 *  original `<button class="stat stat-link">`; before it has run, it still
 *  is one. Matching on the label works in either state and in either ORDER
 *  the two independent observers happen to fire in. "Trades" (ROUND 3) is
 *  never a `.stat-link` on either view, so it never carries the trailing "›"
 *  the strip below exists for — harmless to run on it regardless. */
function findStatCell(wrap: Element, label: "Followers" | "Following" | "Trades"): Element | null {
  for (const cell of Array.from(wrap.querySelectorAll(".stats-row.stats-7 .stat"))) {
    const raw = cell.querySelector(".l.microlabel")?.textContent ?? "";
    // Strips the trailing " ›" statsGrid appends to a still-clickable cell
    // (app.js:1109) — absent once neutralizePublicFollowerTriggers has run.
    if (raw.trim().replace(/\s*›\s*$/, "") === label) return cell;
  }
  return null;
}

function patchStatCell(wrap: Element, label: "Followers" | "Following" | "Trades", value: number): void {
  const cell = findStatCell(wrap, label);
  const n = cell?.querySelector(".n");
  if (!n) {
    console.warn(`[web-install] profile fixup: "${label}" stat cell not found on the public profile`);
    return;
  }
  // 🔑 IDEMPOTENT, AND IT HAS TO BE — same discipline demoTextFixups.ts's
  // fixUnreadBadge already documents for the identical reason: writing
  // `textContent` replaces child nodes, which is a childList mutation
  // regardless of whether the new string equals the old one, and
  // installProfileFixups' own MutationObserver watches `#app`'s subtree
  // with `{childList: true}`. F1's cache re-runs this on every observer
  // pass for a cached handle (see applyProfileStatsFixup's own header) —
  // without this guard, the FIRST cache-hit write would trigger a SECOND
  // observer callback, which would write the SAME value again, triggering
  // a THIRD — a self-sustaining loop that never settles.
  if (n.textContent === String(value)) return;
  n.textContent = String(value);
}

function patchProfileStats(wrap: Element, view: ProfileView, summary: PublicUserProfile): void {
  const sub = wrap.querySelector(".ph-meta .sub");
  if (sub) {
    const text = sub.textContent ?? "";
    if (FOLLOWERS_LINE_RE.test(text)) {
      const next = text.replace(FOLLOWERS_LINE_RE, `${summary.followerCount}$2`);
      // Idempotent — see patchStatCell's own comment for why this matters.
      if (text !== next) sub.textContent = next;
    } else {
      console.warn(
        `[web-install] profile fixup: ".profile .sub" did not match the expected "… N followers" pattern (got "${text}")`,
      );
    }
  }
  patchStatCell(wrap, "Followers", summary.followerCount);
  patchStatCell(wrap, "Following", summary.followingCount);

  const bioEl = wrap.querySelector(".ph-bio");
  const nextBio = summary.bio ?? "";
  // Idempotent — see patchStatCell's own comment for why this matters.
  if (bioEl && bioEl.textContent !== nextBio) bioEl.textContent = nextBio;

  patchLevelChip(wrap, view, summary.gemTier, summary.level);

  // ROUND 3 (card 1218203357125435, "no literals"): Trades, the SEVENTH
  // consumer of the four honest-unknown fields. See PublicUserProfile's own
  // comment for why this can be real even when the floor snapshot's own
  // mapSellerToUser/mapDealCounterparts/mapCommentAuthors could not answer
  // it, and why it must stay optional.
  if (typeof summary.tradeCount === "number") patchStatCell(wrap, "Trades", summary.tradeCount);
}

/** The ONE `.level-chip` per profile view — public renderPublicProfile's
 *  `<span class="level-chip">${esc(tierLabel(u))}</span>` (app.js; empty, and
 *  hidden by .level-chip:empty, when the row's level did not come from points)
 *  and self renderProfile's `<span class="level-chip">${tier.name} ·
 *  ${u.points.toUpperCase()}</span>`. Unlike the badge chips below it
 *  (`.badge-chip`, a different class, rendered only when `u.badges.length`),
 *  this one is unconditional on both views — so no existence check beyond
 *  the querySelector miss is needed. */
function findLevelChip(wrap: Element): Element | null {
  return wrap.querySelector(".ph-row .level-chip");
}

/**
 * ROUND 2, card 1218200003007957's promoted stretch / card 1218128111112081's
 * Q1 ruling: retires the raw trader_level enum (ROOKIE/VERIFIED/HALL_OF_FAME)
 * app.js:1478 prints verbatim, in favor of the real six-gem name.
 *
 * 🔴 F1 (adversarial review): prefers the server's own `gemTier` string —
 * ungated by showPoints, unlike `level` (see this file's ROUND 2 header) —
 * and falls back to deriving the gem from `level` via getGemTier (a copy of
 * data.js's own TIERS ladder, pinned to it by gemTiers.test.ts so a re-band
 * there fails this by name) only for an older API response that carries
 * `level` but not yet `gemTier`.
 *
 * 🔑 BOTH MISSING LEAVES THE CHIP UNTOUCHED — not blanked, not defaulted to
 * EMERALD. Ways this happens, none of them "we know the gem and it's
 * wrong": the GET /api/users/:id fetch hasn't resolved yet, it failed
 * (patchProfileStats is never called on that path at all — see
 * applyProfileStatsFixup's own catch), or — pre-gemTier server — a
 * privacy-hidden `level`. In every case app.js's own chip is the better
 * answer: the real gem when the seller's points rode the wire, and an EMPTY
 * chip (hidden by .level-chip:empty) when they did not — never a fabricated
 * tier. (It printed the raw trader_level word until 2026-09-09, and briefly
 * defaulted to EMERALD for rows with no points, which this paragraph forbids;
 * fixed 2026-09-10.) Exactly PART (a)'s own "a failed fetch paints nothing"
 * rule applied to this field.
 *
 * 🔴 ROUND 3 REGRESSION (card 1218203357125435, "no literals" census): this
 * used to overwrite `chip.textContent` unconditionally on BOTH views. The
 * public chip is only ever the bare gem word (app.js:1478,
 * `${u.level.toUpperCase()}`) so that was correct there — but the SELF
 * view's chip (app.js renderProfile) is `${tier.name} ·
 * ${u.points.toUpperCase()}`, e.g. "EMERALD · 1,450 PTS", and an
 * unconditional overwrite silently dropped the "· N PTS" half — REAL content
 * app.js itself already painted, not a fabrication this module exists to
 * repair. `view` (threaded from ProfileTile, same as everywhere else in this
 * file) is what tells the two shapes apart: on 'self' only the tier token
 * (everything before the FIRST " · ") is replaced, and whatever followed the
 * separator survives verbatim, pass after pass — the same "leave real
 * content standing" rule PART (a)'s own header states for followers/
 * following/bio, applied here to the chip's own second half.
 */
function patchLevelChip(
  wrap: Element,
  view: ProfileView,
  gemTier: string | null | undefined,
  level: number | null | undefined,
): void {
  const gem = typeof gemTier === "string" ? gemTier : typeof level === "number" ? getGemTier(level) : null;
  if (gem === null) return;
  const chip = findLevelChip(wrap);
  if (!chip) return;
  const gemUpper = gem.toUpperCase();
  if (view === "self") {
    // Preserve everything from the first " · " onward — the real, already-
    // rendered points suffix — and replace only the tier token before it.
    const current = chip.textContent ?? "";
    const sepIdx = current.indexOf(" · ");
    const suffix = sepIdx === -1 ? "" : current.slice(sepIdx);
    const next = gemUpper + suffix;
    // Idempotent — see patchStatCell's own comment for why this matters.
    if (chip.textContent === next) return;
    chip.textContent = next;
    return;
  }
  // Idempotent — see patchStatCell's own comment for why this matters.
  if (chip.textContent === gemUpper) return;
  chip.textContent = gemUpper;
}

/**
 * PART (a) — counts + bio, a direct DOM patch with no re-render, exactly
 * ratingFixups.ts's own shape: fetch once per handle, write text into
 * existing nodes, tolerate failure by leaving Blake's own defaults standing.
 *
 * 🔑 UNLIKE ratingFixups.ts, A FAILED FETCH PAINTS NOTHING — not even a
 * fallback state. The ratings tile HAD to be repainted regardless of outcome
 * because its pre-fixup content ("★ 4.9") was itself the fabrication being
 * retired. Here, Blake's own pre-fixup defaults ("—" for followers/
 * following — UNKNOWN_METRIC, data.js, painted from mapSellerToUser's
 * `null` — and an empty bio) are not a fabrication — they are the honest "we
 * don't know yet" state — so a failed fetch simply leaves them standing
 * rather than needing a replacement empty state of its own. The failure is
 * still logged, never silent.
 *
 * 🔑 THIS USED TO SAY "0 followers, 0 following" HERE, AND THAT WAS THE BUG
 * (card 1218203357125435, "no literals"): a literal 0 is indistinguishable
 * from a real zero, so it was never actually the honest state this comment
 * claimed — mapSellerToUser now emits `null` for exactly that reason, and
 * every render site paints null as "—" instead. This function's own
 * behaviour on a failed fetch did not change; what it was leaving standing
 * did.
 *
 * 🔴 F1 (adversarial review, card 1218200003007957): `profileStatsCache`
 * (threaded in the same way `listingsFetchedFor` is, and for the same
 * reason — see this module's own header) is the fix for a full round trip
 * to 0/"" on every re-render. renderPublicProfile() rebuilds ITS ENTIRE
 * subtree from `USERS[handle]` — Blake's own hardcoded zeros — on every one
 * of the `rerenderKeepScroll()`/`render()` calls this module or app.js
 * itself triggers (a tab click, the listings merge below, an unrelated
 * fixup's repaint), which replaces `tile.anchor` with a fresh node that
 * carries no `FIXED_FOR_ATTR` — the ONLY guard this function had before this
 * fix. Un-cached, that reads as "never fetched", so it fetched again AND the
 * visitor saw Blake's zeros for however long that fetch took: a blink on
 * every re-render, plus a GET /api/users/:id that had already succeeded
 * once this tab session. A cache hit patches SYNCHRONOUSLY, inside the same
 * MutationObserver callback that just fired — a microtask, before the
 * browser ever paints the rebuilt subtree — so the reverted defaults are
 * never on screen, not even for a frame, and no second fetch fires.
 */
function applyProfileStatsFixup(tile: ProfileTile, authFetch: AuthFetch, profileStatsCache: Map<string, PublicUserProfile>): void {
  const cached = tile.userId ? profileStatsCache.get(tile.userId) : undefined;
  if (cached) {
    // Re-patch on every pass this handle is cached for, not just the first
    // — the whole point is that a re-render keeps rebuilding Blake's honest
    // zeros into a fresh subtree, and this is what stops them ever painting.
    patchProfileStats(tile.wrap, tile.view, cached);
    return;
  }

  if (tile.anchor.getAttribute(FIXED_FOR_ATTR) === tile.handle) return; // already fetched/fetching for this handle
  tile.anchor.setAttribute(FIXED_FOR_ATTR, tile.handle);

  if (!tile.userId) return; // no resolvable id — silent, see this file's header

  const userId = tile.userId;
  authFetch(`/api/users/${userId}`)
    .then((res) => {
      if (!res.ok) throw new Error(`GET /api/users/${userId} failed: ${res.status} ${res.statusText}`);
      return res.json() as Promise<PublicUserProfile>;
    })
    .then((summary) => {
      // The visitor may have navigated away, or Blake's own render() may
      // have replaced this subtree wholesale for an unrelated reason —
      // same re-check ratingFixups.ts's own callback makes before writing.
      if (!tile.wrap.isConnected || tile.anchor.getAttribute(FIXED_FOR_ATTR) !== tile.handle) return;
      profileStatsCache.set(userId, summary);
      patchProfileStats(tile.wrap, tile.view, summary);
    })
    .catch((err) => {
      console.warn(`[web-install] profile fixup: failed to load profile stats for "${tile.handle}"`, err);
      // Leave the page as-is — see this function's own header for why that,
      // and not a fallback paint, is correct here.
    });
}

/** GET /api/collections/stats/:userId's shape, trimmed to the two fields this
 *  module reads. `itemCount` is a ROUND-2 addition to that route
 *  (routes/collections.ts) — it did not exist when profileFixups.ts's header
 *  was written and card 1218200003007957's stretch was dropped; it and
 *  `collectionValue` are filtered by the SAME hidden-statuses-only rule
 *  (PUBLICLY_HIDDEN_LISTING_STATUSES) so the tile and the chip below always
 *  agree on what they're counting — see collections.ts's own route comment.
 *
 *  🔴 F2 (adversarial review): both fields are OPTIONAL, not just possibly
 *  absent in theory — a server one deploy behind the client (today's wire
 *  shape, mid-rollout: collectionValue present, no itemCount yet) is a real,
 *  expected shape this module must survive without painting `"undefined"`
 *  into either DOM node. patchCollectionStats below gates each half of the
 *  patch on its own `typeof … === "number"` check, same discipline
 *  patchLevelChip already has for `level`/`gemTier`. */
interface CollectionStats {
  collectionValue?: number;
  itemCount?: number;
}

/** Same per-handle "fetched (or in-flight)" guard as FIXED_FOR_ATTR, a
 *  separate attribute because this is an independent fetch with its own
 *  in-flight window — the two can race and land in either order. */
const COLLECTIONS_FIXED_FOR_ATTR = "data-gbz-collstats-for";

/** Verbatim mirror of data.js:499's `fmtVal` — a top-level `const` in a
 *  classic script, not a window property, so this module (compiled
 *  separately, imported as an ES module) has no way to call Blake's own
 *  copy. Kept identical on purpose: a real fetched value must format exactly
 *  like a floor-rendered one, or "$2.1k" turning into "$2100" would be its
 *  own small tell that the number just changed source. */
function fmtVal(n: number): string {
  return n >= 1e6 ? `$${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `$${(n / 1e3).toFixed(1)}k` : `$${n}`;
}

/** The "Collection Value" cell in `.stats-row.stats-7` — app.js:1495. Not a
 *  `.stat-link`/button like Followers/Following, so no trailing "›" to
 *  strip (unlike findStatCell). */
function findCollectionValueTile(wrap: Element): Element | null {
  for (const cell of Array.from(wrap.querySelectorAll(".stats-row.stats-7 .stat"))) {
    if ((cell.querySelector(".l.microlabel")?.textContent ?? "").trim() === "Collection Value") return cell;
  }
  return null;
}

/**
 * Patches the Collection Value tile plus the Collection section's own count
 * chip + value line (app.js:1507-1509's `.exch-banner-toprow`) — the "N
 * items · $value" family task brief calls out by name. Both read
 * `collectionFor`/`collectionValueOf`, so both need the write, and both need
 * the SAME idempotent-write / cache-hit-repatch discipline patchProfileStats
 * already established: renderPublicProfile() recomputes every one of them
 * fresh from LISTINGS/PUBLIC_EXTRAS on every re-render, so an un-cached
 * direct patch would revert on the very next tab click — F1's bug, applied
 * to a second value.
 */
function patchCollectionStats(wrap: Element, stats: CollectionStats): void {
  // F2 (adversarial review): each half gated independently — a response
  // missing one field (deploy skew, see CollectionStats's own comment) must
  // patch the half it DOES have real data for, without painting
  // "undefined"/"NaN" into the half it doesn't.
  if (typeof stats.collectionValue === "number") {
    const valueTile = findCollectionValueTile(wrap);
    const valueN = valueTile?.querySelector(".n");
    const nextValue = fmtVal(stats.collectionValue);
    // Idempotent — see patchStatCell's own comment for why this matters.
    if (valueN && valueN.textContent !== nextValue) valueN.textContent = nextValue;

    const secCount = wrap.querySelector(".exch-banner-toprow .sec-count");
    const nextSec = `${nextValue} collection value`;
    if (secCount && secCount.textContent !== nextSec) secCount.textContent = nextSec;
  }

  if (typeof stats.itemCount === "number") {
    const countChip = wrap.querySelector(".exch-banner-toprow .count-chip");
    const nextCount = String(stats.itemCount);
    if (countChip && countChip.textContent !== nextCount) countChip.textContent = nextCount;
  }
}

/**
 * PART (c), ROUND 2 — the Collection Value tile and count chip, fetched from
 * the real, public GET /api/collections/stats/:userId and direct-patched
 * exactly like PART (a)'s followers/following/bio: fetch once per handle,
 * cache so a re-render never reverts to Blake's floor numbers, never paint
 * on failure (the pre-fixup floor value isn't a fabrication demanding
 * replacement, same reasoning as PART (a)'s own header).
 *
 * The GRID below the count chip (`.mgr-grid`) is NOT completed by this
 * fixup — see this file's own ROUND 2 header note for why merging real
 * per-user collection ITEMS needs a new row mapper this round didn't build,
 * and is disclosed as a residual rather than attempted here.
 */
function applyProfileCollectionsFixup(
  tile: ProfileTile,
  authFetch: AuthFetch,
  collectionStatsCache: Map<string, CollectionStats>,
): void {
  const cached = tile.userId ? collectionStatsCache.get(tile.userId) : undefined;
  if (cached) {
    patchCollectionStats(tile.wrap, cached);
    return;
  }

  if (tile.anchor.getAttribute(COLLECTIONS_FIXED_FOR_ATTR) === tile.handle) return; // already fetched/fetching
  tile.anchor.setAttribute(COLLECTIONS_FIXED_FOR_ATTR, tile.handle);

  if (!tile.userId) return; // no resolvable id — silent, see this file's header

  const userId = tile.userId;
  authFetch(`/api/collections/stats/${userId}`)
    .then((res) => {
      if (!res.ok) throw new Error(`GET /api/collections/stats/${userId} failed: ${res.status} ${res.statusText}`);
      return res.json() as Promise<CollectionStats>;
    })
    .then((stats) => {
      if (!tile.wrap.isConnected || tile.anchor.getAttribute(COLLECTIONS_FIXED_FOR_ATTR) !== tile.handle) return;
      collectionStatsCache.set(userId, stats);
      patchCollectionStats(tile.wrap, stats);
    })
    .catch((err) => {
      console.warn(`[web-install] profile fixup: failed to load collection stats for "${tile.handle}"`, err);
      // Leave the page as-is — see applyProfileStatsFixup's own header for
      // why that, and not a fallback paint, is correct here.
    });
}

interface ProfileWindow extends Window {
  // Returns whether the row was newly pushed (false for an id already on
  // the floor) — floorTransport.ts's own bridge, mirrored here so F4 below
  // can tally how many of a page's rows actually changed LISTINGS.
  __gbzMergeListing?: (listing: DemoListing, user: DemoUser) => boolean;
  // PUBLIC_EXTRAS[handle]'s merge sibling — F3 (adversarial review). Returns
  // whether the item was newly pushed, same convention as __gbzMergeListing
  // above. See its own header (floorTransport.ts's bridgeScriptSource) for
  // why the Collection grid merges here and not into LISTINGS.
  __gbzMergeCollectionExtra?: (handle: string, item: PublicCollectionExtraItem) => boolean;
  // USER_LOOKING[handle] setter — floorTransport.ts's own bridge. Returns the
  // label count, same "did this write anything" convention as the two merge
  // functions above, so PART (e) below can skip a repaint at zero.
  __gbzSetUserLooking?: (handle: string, rows: ApiWant[]) => number;
  rerenderKeepScroll?: () => void;
  render?: () => void;
}

/**
 * PART (b) — the full listings set, merged through the EXISTING bridge and
 * then repainted through Blake's OWN renderer, never a hand-rolled one.
 *
 * WHY A RE-RENDER AT ALL, when every other fixup in this codebase is a
 * direct DOM patch. `theirs` (the Active Items count) and the "Live
 * Listings" grid are both computed INLINE inside renderPublicProfile() —
 * `LISTINGS.filter((l) => l.seller === handle)`, recomputed fresh on every
 * call, never stored anywhere this module could patch after the fact.
 * Merging new rows into LISTINGS (via the bridge) changes what THAT
 * computation would produce on its NEXT run, but changes nothing already on
 * screen — the only way to make the tile and grid catch up is to make
 * Blake's own renderer run again, the same reason installRealDiscoverSearch
 * (floorTransport.ts) re-dispatches an "input" event to force his OWN
 * listener to recompute rather than re-deriving discSearchData's answer by
 * hand. `rerenderKeepScroll()` (else `render()`) is the same house
 * mechanism listingVotes.ts/commentPost.ts already use to
 * force a repaint after an async write lands — "the same re-render
 * mechanism ratingFixups.ts uses" doesn't refer to a re-render at all (that
 * module never triggers one); it refers to the SHAPE both share: the
 * profile the visitor is currently looking at updates once, asynchronously,
 * without navigating them anywhere.
 *
 * 🔑 WHY A CLOSURE Set AND NOT A DOM ATTRIBUTE, unlike every other guard in
 * this file and in ratingFixups.ts. This action is the only one of the two
 * that forces a FULL re-render, which replaces the entire `#app` subtree —
 * including `[data-follow]` itself — wiping any DOM-attribute guard along
 * with it. A DOM-attribute guard here would re-arm on the very re-render it
 * just caused: the next observer callback would see a "fresh" (unmarked)
 * `[data-follow]`, refetch, re-merge (harmless — the bridge is first-write-
 * wins) and, unless something stopped it, re-render AGAIN — a self-sustaining
 * loop. Keying by `userId` in a Set held in installProfileFixups' closure
 * survives the re-render (it isn't DOM state at all) and fetches this
 * seller's listings AT MOST ONCE per tab session — which also means
 * navigating away and back to the same seller does not refetch, but does
 * not need to: whatever was merged the first time is already sitting in
 * LISTINGS, and a plain navigation-triggered render() picks it up for free.
 *
 * THE MERGE'S `user` ARGUMENT IS A STUB, not a fully-populated DemoUser.
 * `GET /api/users/:id/listings` returns bare listing rows with no seller
 * info at all (unlike GET /api/listings' `{listing, seller}` envelope), so
 * there is nothing here to build a real one from. `__gbzMergeListing` only
 * ever WRITES its `user` argument into USERS when `USERS[handle]` is
 * absent — and a handle can only reach this function at all via a
 * resolvable `userIds[handle]`, which (per ratingFixups.ts's own comment on
 * that map's two producers) already implies `USERS[handle]` exists. So in
 * the path this code actually runs, the stub is inert by construction; it
 * exists only to satisfy mapListingRow's type in the one drifted-producer
 * case ratingFixups.ts already flags as a louder problem than this stub
 * could cause.
 *
 * 🔴 F3 (adversarial review): `listingsFetchedFor.add(userId)` happens
 * BEFORE the fetch, deliberately — it is the in-flight guard that stops a
 * second MutationObserver pass from firing a concurrent second fetch for
 * the same seller while the first is still pending. A transient failure
 * (network blip, a 500) must not leave that guard standing forever, though
 * — RETRY SEMANTICS: `.catch()` below deletes the entry it added, so the
 * seller is not poisoned for the rest of the tab session; the very next
 * MutationObserver pass (any unrelated mutation on this profile) retries
 * the fetch exactly as if it had never run.
 *
 * 🔴 F4 (adversarial review): `rerenderKeepScroll()`/`render()` used to
 * fire unconditionally once the fetch resolved, even when nothing about
 * LISTINGS actually changed — an empty `rows` response, a page that is
 * ALL duplicates of what an earlier pass already merged, or ALL rows in a
 * category app.js can't render, all used to still blink the view and
 * re-run app.js's whole render pipeline for zero new content. `addedCount`
 * (tallied from `__gbzMergeListing`'s own boolean return, floorTransport.ts)
 * skips the repaint at zero — the same house convention
 * `__gbzSetListingComments` returns a boolean for.
 */
function applyProfileListingsFixup(
  tile: ProfileTile,
  win: Window,
  authFetch: AuthFetch,
  listingsFetchedFor: Set<string>,
): void {
  if (!tile.userId) return; // no resolvable id — silent, see this file's header
  if (listingsFetchedFor.has(tile.userId)) return; // already fetched (or in flight) this tab session
  listingsFetchedFor.add(tile.userId);

  const { handle, userId } = tile;

  authFetch(`/api/users/${userId}/listings?limit=100`)
    .then((res) => {
      if (!res.ok) throw new Error(`GET /api/users/${userId}/listings failed: ${res.status} ${res.statusText}`);
      return res.json() as Promise<ApiListingRow["listing"][]>;
    })
    .then((rows) => {
      const merge = (win as ProfileWindow).__gbzMergeListing;
      if (typeof merge !== "function") {
        console.warn("[web-install] profile fixup: bridge did not define window.__gbzMergeListing — listings not merged");
        return;
      }
      // Built here, not at module scope — same lazy-access convention
      // demoTextFixups.ts already uses for this same export (only read
      // inside a function body, never at import time), so a test that
      // mocks floorTransport.ts down to a handful of named exports doesn't
      // need to also stub this one just because this module imports it.
      const renderableCategories = new Set(RENDERABLE_CATEGORY_SLUGS);
      let addedCount = 0;
      for (const raw of rows) {
        if (!renderableCategories.has(raw.category)) continue; // same crash guard installRealDiscoverSearch uses
        const { listing, user } = mapListingRow({
          listing: raw,
          seller: {
            id: userId,
            username: handle,
            displayName: null,
            avatar: null,
            traderLevel: null,
            tradeCount: null,
            createdAt: null,
          },
        });
        if (merge(listing, user)) addedCount++;
      }

      // F4: an empty response, an all-duplicates response, or an
      // all-unrenderable one all leave LISTINGS exactly as it was — nothing
      // for a repaint to show, so skip it rather than blink the view for no
      // reason. See this function's own header for the full reasoning.
      if (addedCount === 0) return;

      // Repaint only if the visitor is still looking at THIS profile — a
      // fetch that resolves after they navigated away must not force an
      // unrelated view to repaint. Re-queried fresh rather than reusing
      // `tile`, because `tile.anchor` may itself have been replaced by an
      // unrelated re-render in the meantime.
      const current = win.document.querySelector(".profile.wrap [data-follow]");
      if (!current || current.getAttribute("data-follow") !== handle) return;

      const w = win as ProfileWindow;
      // rerenderKeepScroll, not render: this view sits below the fold on
      // plenty of profiles, and render() alone would send the visitor back to
      // the top of it.
      if (typeof w.rerenderKeepScroll === "function") w.rerenderKeepScroll();
      else w.render?.();
    })
    .catch((err) => {
      // F3: delete, not leave standing — a transient failure here must not
      // poison this seller for the rest of the tab session. The next
      // MutationObserver pass (any unrelated mutation on this profile)
      // retries the fetch exactly as if it had never run.
      listingsFetchedFor.delete(userId);
      console.warn(`[web-install] profile fixup: failed to load listings for "${handle}"`, err);
    });
}

/**
 * PART (d), F3 (adversarial review, card 1218200003007957) — the real fix
 * for the Collection section's own grid (`.mgr-grid`), the residual this
 * card's ROUND 2 originally disclosed and dropped: it used to render only
 * whatever collectionFor(handle) could piece together from active LISTINGS
 * (PART (b)) plus a handful of demo-only PUBLIC_EXTRAS rows, so a real
 * seller's sold/traded/pending/collection-only items were invisible while
 * the count chip and value tile (PART (c) above) already told the truth
 * about them — a "14" chip over 2 visible cards.
 *
 * Fetches the SAME public route PART (c)'s numbers are computed from
 * server-side (GET /api/collections/user/:userId — collections.ts:34,
 * :655-659's hidden-statuses-only filter, the same one mobile's own grid and
 * collection-value total already key off), maps each row through
 * mapPublicCollectionRow (floorTransport.ts), and merges every non-null
 * result into PUBLIC_EXTRAS[handle] via the bridge's own
 * __gbzMergeCollectionExtra — see that function's header
 * (floorTransport.ts's bridgeScriptSource) for why PUBLIC_EXTRAS, not
 * LISTINGS, is the merge target: collectionFor(handle)'s LISTINGS-derived
 * `live` branch can only ever label a row ACTIVE or IN COLLECTION, with no
 * way to express SOLD/TRADED/PENDING at all. An "active"-status row maps to
 * null on purpose — it is already on LISTINGS via PART (b)'s own merge, and
 * mapping it here too would double-count it.
 *
 * Same re-render shape as PART (b) and the same reasons: merging changes
 * what collectionFor(handle) would produce on ITS NEXT call, not anything
 * already on screen, so Blake's own renderer has to run again — and a
 * closure Set, not a DOM attribute, survives that full re-render the way
 * applyProfileListingsFixup's own header explains. F4's own discipline
 * applies a second time too: `addedCount` skips the repaint when nothing
 * was actually merged.
 */
function applyProfileCollectionGridFixup(
  tile: ProfileTile,
  win: Window,
  authFetch: AuthFetch,
  collectionGridFetchedFor: Set<string>,
): void {
  if (!tile.userId) return; // no resolvable id — silent, see this file's header
  if (collectionGridFetchedFor.has(tile.userId)) return; // already fetched (or in flight) this tab session
  collectionGridFetchedFor.add(tile.userId);

  const { handle, userId } = tile;

  authFetch(`/api/collections/user/${userId}`)
    .then((res) => {
      if (!res.ok) throw new Error(`GET /api/collections/user/${userId} failed: ${res.status} ${res.statusText}`);
      return res.json() as Promise<PublicCollectionRow[]>;
    })
    .then((rows) => {
      const merge = (win as ProfileWindow).__gbzMergeCollectionExtra;
      if (typeof merge !== "function") {
        console.warn("[web-install] profile fixup: bridge did not define window.__gbzMergeCollectionExtra — collection grid not merged");
        return;
      }
      let addedCount = 0;
      for (const row of rows) {
        const mapped = mapPublicCollectionRow(row);
        if (!mapped) continue; // active row (already on LISTINGS) or an unbucketed status — see the mapper's own comment
        if (merge(handle, mapped)) addedCount++;
      }

      if (addedCount === 0) return; // nothing new — see this function's own header

      // Repaint only if the visitor is still looking at THIS profile — same
      // re-check applyProfileListingsFixup's own callback makes.
      const current = win.document.querySelector(".profile.wrap [data-follow]");
      if (!current || current.getAttribute("data-follow") !== handle) return;

      const w = win as ProfileWindow;
      if (typeof w.rerenderKeepScroll === "function") w.rerenderKeepScroll();
      else w.render?.();
    })
    .catch((err) => {
      // Same retry semantics as applyProfileListingsFixup's own F3 fix: a
      // transient failure must not poison this seller for the rest of the
      // tab session.
      collectionGridFetchedFor.delete(userId);
      console.warn(`[web-install] profile fixup: failed to load collection grid items for "${handle}"`, err);
    });
}

/**
 * PART (e) — card 1218203357125435 ("no literals"), item 6: the PUBLIC
 * profile's "@handle Is Looking For" section. renderPublicProfile reads
 * `USER_LOOKING[handle] || []` (app.js), and USER_LOOKING is cleared to `{}`
 * at boot and never repopulated by anything else — see floorTransport.ts's
 * "13 bindings" comment. GET /api/wants/:userId (routes/wants.ts,
 * optionalAuth) is real and public, so this wires it, fetch-once-per-handle
 * and re-render on arrival — the same shape as PART (b)/(d) above, for the
 * same reason: the section is computed INLINE inside renderPublicProfile
 * from a global this module cannot patch a DOM node for directly.
 *
 * PUBLIC VIEW ONLY. The self view's own Looking For section reads
 * `myLookingFor`, a DIFFERENT global entirely, already kept current by
 * installWants (floorTransport.ts) — a boot-time, once-per-session fetch
 * from an entirely separate call site (WebInstall.tsx), not this per-view
 * MutationObserver. Fetching wants again here for the self view would be an
 * inert, wasted round trip: nothing on the self view ever reads
 * USER_LOOKING.
 *
 * 🔑 THE FIXTURE CANNOT RESURFACE THROUGH THIS PATH. USER_LOOKING[handle] is
 * OVERWRITTEN wholesale with server-sourced labels every time this resolves
 * — never merged with, never falls back to, whatever the boot-clear left
 * behind. Card 1218203357125435's own guard ("never a real user") holds by
 * construction: there is no code left, anywhere, that ever writes one of
 * Blake's original six fixture arrays into USER_LOOKING.
 */
function applyProfileWantsFixup(
  tile: ProfileTile,
  win: Window,
  authFetch: AuthFetch,
  wantsFetchedFor: Set<string>,
): void {
  if (tile.view !== "public") return; // self reads myLookingFor, not USER_LOOKING — see this function's own header
  if (!tile.userId) return; // no resolvable id — silent, see this file's header
  if (wantsFetchedFor.has(tile.userId)) return; // already fetched (or in flight) this tab session
  wantsFetchedFor.add(tile.userId);

  const { handle, userId } = tile;

  authFetch(`/api/wants/${userId}`)
    .then((res) => {
      if (!res.ok) throw new Error(`GET /api/wants/${userId} failed: ${res.status} ${res.statusText}`);
      return res.json() as Promise<ApiWant[]>;
    })
    .then((rows) => {
      const setter = (win as ProfileWindow).__gbzSetUserLooking;
      if (typeof setter !== "function") {
        console.warn("[web-install] profile fixup: bridge did not define window.__gbzSetUserLooking — looking-for not merged");
        return;
      }
      const count = setter(handle, Array.isArray(rows) ? rows : []);
      if (count === 0) return; // nothing to show — same F4 discipline as PART (b)/(d) above

      // Repaint only if the visitor is still looking at THIS profile — same
      // re-check applyProfileListingsFixup's own callback makes.
      const current = win.document.querySelector(".profile.wrap [data-follow]");
      if (!current || current.getAttribute("data-follow") !== handle) return;

      const w = win as ProfileWindow;
      if (typeof w.rerenderKeepScroll === "function") w.rerenderKeepScroll();
      else w.render?.();
    })
    .catch((err) => {
      // Same retry semantics as applyProfileListingsFixup's own F3 fix.
      wantsFetchedFor.delete(userId);
      console.warn(`[web-install] profile fixup: failed to load looking-for labels for "${handle}"`, err);
    });
}

/** The pure, directly-testable core — same role applyRatingFixup plays for
 *  ratingFixups.ts. `listingsFetchedFor`, `profileStatsCache`, and (ROUND 2)
 *  `collectionStatsCache` are all threaded in rather than held internally so
 *  a test can assert their own idempotency without going through the
 *  observer (pass the same instance twice) or cross-handle behaviour (pass a
 *  fresh one). installProfileFixups below is the only real caller that needs
 *  to own any of them for the life of the tab. `profileStatsCache` is F1's
 *  fix (adversarial review, card 1218200003007957) — see
 *  applyProfileStatsFixup's own header. `collectionStatsCache` is the same
 *  fix applied to the Collection Value tile/count chip — see
 *  applyProfileCollectionsFixup's own header.
 *
 *  `collectionGridFetchedFor` (F3) and `wantsFetchedFor` (PART (e)) are the
 *  parameters here with a default — surgical choice, not an oversight: this
 *  function already has call sites' worth of existing tests passing fewer
 *  positional arguments, none of which exercise those two fixups at all.
 *  Defaulting each to a fresh Set keeps every one of those compiling and
 *  passing unchanged; installProfileFixups below still passes its OWN
 *  tab-lifetime Sets explicitly, exactly like the other guards, so
 *  production behaviour is unaffected. */
export function applyProfileFixup(
  doc: Document,
  win: Window,
  authFetch: AuthFetch,
  userIds: Record<string, string>,
  listingsFetchedFor: Set<string>,
  profileStatsCache: Map<string, PublicUserProfile>,
  collectionStatsCache: Map<string, CollectionStats>,
  collectionGridFetchedFor: Set<string> = new Set(),
  selfUserId: string | null = null,
  wantsFetchedFor: Set<string> = new Set(),
): void {
  const tile = findProfileTile(doc, userIds, selfUserId);
  if (!tile) return; // not on the public profile view this pass — legitimate absence

  applyProfileStatsFixup(tile, authFetch, profileStatsCache);
  applyProfileListingsFixup(tile, win, authFetch, listingsFetchedFor);
  applyProfileCollectionsFixup(tile, authFetch, collectionStatsCache);
  applyProfileCollectionGridFixup(tile, win, authFetch, collectionGridFetchedFor);
  applyProfileWantsFixup(tile, win, authFetch, wantsFetchedFor);
}

/** Must run after app.js has loaded, same ordering requirement as
 *  installRatingFixups — see WebInstall.tsx's call site. `userIds` is the
 *  SAME map ratingFixups.ts/installFollowUser hold, by reference — see
 *  ratingFixups.ts's own comment on why a late-arriving comment author still
 *  resolves. */
export function installProfileFixups(
  win: Window,
  authFetch: AuthFetch,
  userIds: Record<string, string>,
  /** The signed-in user’s id, or null when signed out. Feeds the SELF view;
   *  the public view resolves its id through `userIds` as before. */
  selfUserId: string | null = null,
): void {
  const doc = win.document;
  const app = doc.getElementById("app");
  if (!app) {
    console.warn("[web-install] installProfileFixups: #app not found, profile fixups not installed");
    return;
  }
  // Closure state, not a DOM attribute — see applyProfileListingsFixup's own
  // header for why the listings half specifically needs this rather than
  // the FIXED_FOR_ATTR pattern the stats half uses.
  const listingsFetchedFor = new Set<string>();
  // F1's cache, held for the life of the tab exactly like listingsFetchedFor
  // above — see applyProfileStatsFixup's own header for why the stats half
  // needs one too.
  const profileStatsCache = new Map<string, PublicUserProfile>();
  // ROUND 2's cache, same reasoning, for the Collection Value tile/count
  // chip — see applyProfileCollectionsFixup's own header.
  const collectionStatsCache = new Map<string, CollectionStats>();
  // F3's guard, same closure-not-attribute reasoning as listingsFetchedFor
  // above — see applyProfileCollectionGridFixup's own header.
  const collectionGridFetchedFor = new Set<string>();
  // PART (e)'s guard, same reasoning — see applyProfileWantsFixup's own header.
  const wantsFetchedFor = new Set<string>();
  applyProfileFixup(doc, win, authFetch, userIds, listingsFetchedFor, profileStatsCache, collectionStatsCache, collectionGridFetchedFor, selfUserId, wantsFetchedFor);
  const observer = new MutationObserver(() => {
    applyProfileFixup(doc, win, authFetch, userIds, listingsFetchedFor, profileStatsCache, collectionStatsCache, collectionGridFetchedFor, selfUserId, wantsFetchedFor);
  });
  observer.observe(app, { childList: true, subtree: true });
}
